import { Clipboard } from '@angular/cdk/clipboard';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { colorGallery } from './constants';
import { PaeSelectionColors } from 'src/app/pae-view/pae-view.component';
import { Chain } from 'src/app/pae-view/pae-data.service';
import { EntryData } from 'src/app/entry/structure-data.models';
@Injectable({
  providedIn: 'root',
})
export class UtilService {
  private clipboard = inject(Clipboard);

  public copy(value: string): void {
    this.clipboard.copy(value);
  }

  public extractDomainNumber(input: string): number | undefined {
    const parts = input.split(" ");
    const lastPart = parts[parts.length - 1];
    const num = parseInt(lastPart, 10);
    return isNaN(num) ? undefined : num;
  }

  public fetchColorByIndex(index: number): string {
    return colorGallery[index % colorGallery.length];
  }

  public areDatesDifferent(date1: string, date2: string): boolean {
    const d1 = new Date(date1).toISOString().split('T')[0];
    const d2 = new Date(date2).toISOString().split('T')[0];
    return d1 !== d2;
  }

  public hexToRgb(hex): string {
    // Remove the hash symbol if present
    hex = hex.replace(/^#/, '');

    // Parse the hex color based on its length
    let bigint;
    if (hex.length === 3) {
      // Handle shorthand hex color (e.g., #abc)
      bigint = parseInt(
        hex
          .split('')
          .map((c) => c + c)
          .join(''),
        16
      );
    } else if (hex.length === 6) {
      // Handle full hex color (e.g., #aabbcc)
      bigint = parseInt(hex, 16);
    } else {
      throw new Error('Invalid hex color format');
    }

    // Extract the RGB values
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgb(${r}, ${g}, ${b})`;
  }

  private isDomainTrackClicked = new BehaviorSubject<string>(null);
  public domainTrackClicked = this.isDomainTrackClicked.asObservable();

  public clickDomainTrack(state: string): void {
    this.isDomainTrackClicked.next(state);
  }

  public removeVersionFromAccession(param: string): string {
    return param.replace(/-v\d+$/, '');
  }

  public extractUniprotAccession(input: string): string {
    const match = input.match(/^AF-(.+?)-F\d+$/);
    return match ? match[1] : input;
  }

  // Groups per-chain prediction-API rows (macromoleculesData) into distinct macromolecules.
  // uniprotAccession alone isn't a reliable identity: a polyprotein cleaved into separate
  // chains (e.g. NS3/NS5) can report the SAME accession for two genuinely different chains
  // with different residue ranges (AFPDBE-1435). uniprotDescription + start position
  // disambiguates that case while still collapsing an ordinary homodimer's identical chains
  // (same accession, same description, same start) into one group.
  public groupMacromoleculeChains(chains: EntryData[]): EntryData[][] {
    const groups = new Map<string, EntryData[]>();
    for (const item of chains || []) {
      const accession = item?.uniprotAccession || '';
      const description = item?.uniprotDescription || '';
      const start = item?.uniprotStart ?? item?.sequenceStart ?? '';
      const key = `${accession}|${description}|${start}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)?.push(item);
    }
    return Array.from(groups.values());
  }

  // Chain's own residue range, preferring the true UniProt-numbered start/end and falling
  // back to sequenceStart/sequenceEnd only when uniprotStart/uniprotEnd aren't populated.
  public getMacromoleculeResidueRange(item: EntryData): { start: number; end: number } | null {
    const start = item?.uniprotStart ?? item?.sequenceStart;
    const end = item?.uniprotEnd ?? item?.sequenceEnd;
    if (start === undefined || start === null || end === undefined || end === null) {
      return null;
    }
    return { start, end };
  }

  private getSelectionDataForRegion(region: { start: number, end: number }, chains: Chain[]) {
    const start = region.start; // 0-based start inclusive
    const stop = region.end + 1; // 0-based end exclusive
    let offset = 0;
    const out: { start_residue_number: number, end_residue_number: number, struct_asym_id?: string }[] = []; // PDBe Molstar QueryParam[]
    for (const chain of chains) {
      if (stop > offset && start < offset + chain.length) {
        const startInChain = Math.max(start - offset, 0);
        const stopInChain = Math.min(stop - offset, chain.length);
        out.push({
          struct_asym_id: chain.label_asym_id,
          start_residue_number: startInChain + 1, // 1-based start inclusive
          end_residue_number: stopInChain, // 1-based end inclusive
        })
      } // else chain not selected
      offset += chain.length;
    }
    return out;
  }

  public getSelectionData(regions: { start: number, end: number }[], chains: Chain[] = [{ name: 'XXX', label_asym_id: undefined, length: Infinity, sequenceEnd: Infinity, sequenceStart:1 }]) {
    const [xRegion, yRegion] = regions;
    const selection = [];

    const xSelection = this
      .getSelectionDataForRegion(xRegion, chains)
      .map(sel => ({ ...sel, color: PaeSelectionColors.X, class: 'ms-pae-select-orange', focus: true }));
    selection.push(...xSelection);

    const ySelection = this
      .getSelectionDataForRegion(yRegion, chains)
      .map(sel => ({ ...sel, color: PaeSelectionColors.Y, class: 'ms-pae-select-green', focus: true }));
    selection.push(...ySelection);

    const overlapRegion = { start: Math.max(xRegion.start, yRegion.start), end: Math.min(xRegion.end, yRegion.end) };
    if (overlapRegion.start <= overlapRegion.end) {
      // Add overlap
      const overlapSelection = this
        .getSelectionDataForRegion(overlapRegion, chains)
        .map(sel => ({ ...sel, color: PaeSelectionColors.Overlap, class: 'ms-pae-select-yellow', focus: true }));
      selection.push(...overlapSelection);
    }
    return selection;
  }
}
